import gql from 'graphql-tag'

export const USER_FRAGMENT = gql`
  fragment CoreUserFields on User {
    id
    email
    slug
    username
    firstName
    lastName
    persona
    ageOfConsent
    confirmedAt
    archivedAt
    guardianEmail
    vpcSchool
    invitedBy {
      email
      username
    }
    roles {
      name
    }
    registrationChannel
    schools {
      name
    }
    preferences {
      language
      signature
      toc
      privacyPolicy
      eduConsent
    }
    rosters {
      aliasStudentName
      classroom {
        code
      }
    }
    memberships {
      id
    }
    assignments {
      id
      slug
    }
  }`

export const USER_QUERY = gql`
  ${USER_FRAGMENT}
  query User {
    user {
      ...CoreUserFields
    }
  }
`

export const CHILDREN_QUERY = gql`
  query myChildren {
    myChildren {
      id
      username
      firstName
      slug
      preferences { gradeLevels }
    }
  }
`

export const USER_NOTIFICATIONS = gql`
  query notifications {
    notifications {
      id
      user {
        id
        slug
        email
      }
      notificationType
      shortMessage
    }
  }
`

export const RESET_PASSWORD = gql`
  mutation resetPassword($email: String!) {
    resetPassword(email: $email) {
      status
      errors
      user {
        id
        email
        slug
        username
        firstName
        lastName
      }
    }
  }
`

export const RESET_FORGOTTEN_PASSWORD = gql`
  mutation resetForgottenPassword($resetPasswordToken: String!, $password: String!) {
    resetForgottenPassword(resetPasswordToken: $resetPasswordToken, password: $password) {
      status
      errors
      user {
        id
        email
        slug
        username
        firstName
        lastName
      }
    }
  }
`

export const CREATE_USER = gql`
  ${USER_FRAGMENT}
  mutation CreateUserProfile($email: String!, $username: String, $password: String, $ageOfConsent: Boolean
    $firstName: String, $lastName: String, $organizationId: Int, $persona: String,
    $preferences: UserPreferencesInput, $vpcSchool: String) {
    createUserProfile(
      email: $email,
      username: $username,
      password: $password,
      ageOfConsent: $ageOfConsent,
      firstName: $firstName,
      lastName: $lastName,
      organizationId: $organizationId,
      persona: $persona,
      preferences: $preferences,
      vpcSchool: $vpcSchool
    )
    {
      status
      errors
      token
      user {
        ...CoreUserFields
      }
    }
  }
`

export const UPSERT_CHILD = gql`
  mutation UpsertChild($guardianEmail: String!, $username: String!, $firstName: String!, $gradeLevels: String) {
    upsertChild(
      guardianEmail: $guardianEmail,
      username: $username,
      firstName: $firstName,
      gradeLevels: $gradeLevels
    ) {
      status
      errors
      child {
        guardianEmail
        firstName
        username
        preferences { gradeLevels }
        persona
        slug
      }
    }
  }
`

export const INVITE_PARENT = gql`
  mutation InviteParent($email: String!) {
    inviteGuardian(email: $email) {
      status
      user {
        id
        slug
        email
        persona
      }
      errors
    }
  }
`

export const REMOVE_USER = gql`
  mutation RemoveUser($slug: String!) {
    removeUser(slug: $slug) {
      status
      errors
      user {
        id
        username
      }
    }
  }
`

export const REMOVE_USER_AUTHENTICATION = gql`
  mutation RemoveUserAuthentication ($uid: String!, $email: String, $provider: String!) {
    removeUserAuthentication (uid: $uid, email: $email, provider: $provider) {
      status
      errors
    }
  }`

export const REMOVE_NOTIFICATION = gql`
  mutation RemoveNotification($id: Int!) {
    removeNotification(id: $id) {
      status
      errors
    }
  }
`

export const CLASSCODE_LOGIN = gql`
  mutation ClassroomSignIn($classcode: String!, $firstName: String!, $lastName: String!) {
    classroomSignIn(classcode: $classcode, firstName: $firstName, lastName: $lastName) {
      status
      errors
      classroom {
        id
        name
        code
        classType
        organization {
          id
          name
        }
        students {
          id
          firstName
          lastName
        }
      }
      authentication {
        token
        user {
          id
          username
          firstName
          lastName
          persona
          confirmedAt
        }
      }
    }
  }
`

export const CREATE_STUDENT_BY_CLASSROOM = gql`
  mutation createStudentByClassroom($classroomCode: String!, $firstName: String!, $lastName: String!, $ignoreDuplication: Boolean) {
    createStudentByClassroom(classroomCode: $classroomCode, firstName: $firstName, lastName: $lastName, ignoreDuplication: $ignoreDuplication) {
      status
      errors
      classroom {
        id
        name
        code
        classType
        organization {
          id
          name
        }
        students {
          id
          firstName
          lastName
        }
      }
      student {
        id
        username
        firstName
        lastName
        persona
        slug
        archivedAt
        rosters {
          aliasStudentName
          classroom {
            code
          }
        }
      }
    }
  }
`

export const LOGIN_MUTATION = gql`
  ${USER_FRAGMENT}
  mutation SignIn($login: String!, $password: String!) {
    signIn(login: $login, password: $password) {
      token
      status
      errors
      user {
        ...CoreUserFields
      }
    }
  }
`

export const SOCIAL_LOGIN_MUTATION = gql`
  ${USER_FRAGMENT}
  mutation SocialSignIn(
    $provider: String!
    $uid: String!
    $accessToken: String!
  ) {
    socialSignIn(uid: $uid, provider: $provider, accessToken: $accessToken) {
      token
      status
      user {
        ...CoreUserFields
      }
    }
  }
`
export const SIGNIN_BY_TOKEN = gql`
  ${USER_FRAGMENT}
  mutation SignInByToken($token: String!) {
    signInByToken(token: $token) {
      token
      status
      errors
      user {
        ...CoreUserFields
      }
    }
  }
`

export const SIGNUP_SOCIAL_MUTATION = gql`
  ${USER_FRAGMENT}
  mutation RegisterSocialUser(
    $provider: String!
    $uid: String!
    $email: String
    $username: String
    $firstName: String
    $lastName: String
  ) {
    registerSocialUser(
      registrationDetails: {
        email: $email
        uid: $uid
        provider: $provider
        username: $username
        firstName: $firstName
        lastName: $lastName
      }
    ) {
      user {
        ...CoreUserFields
      }
      token
    }
  }
`

export const RESEND_VERIFICATION_EMAIL = gql`
  mutation resendConfirmation($email: String!) {
    resendConfirmation(email: $email) {
      status
      errors
    }
  }
`
export const GOOGLE_SYNC_CLASSROOMS = gql`
  query classroomsWithGoogle {
    classroomsWithGoogle {
      name
      id
    }
  }
`
export const VERIFY_GOOGLE_SYNC_AUTHENTICATION = gql`
  query googleClassroomAuthentications {
    googleClassroomAuthentications {
      accessToken
      refreshToken
      provider
      email
    }
  }
`
export const UPSERT_GOOGLE_SYNC_AUTHENTICATION = gql`
  mutation upsertUserAuthentication($uid: String!, $provider: String!, $accessToken: String, $refreshToken: String!, $email: String) {
    upsertUserAuthentication(uid: $uid, provider: $provider, accessToken: $accessToken, refreshToken: $refreshToken, email: $email) {
      status
      errors
      token
    }
  }
`
export const UPSERT_USER_PROFILE = gql`
  ${USER_FRAGMENT}
  mutation updateUserProfile($email: String!, $username: String, $guardianEmail: String, $password: String,
    $firstName: String, $lastName: String, $persona: String, $profile: String, $ageOfConsent: Boolean
    $preferences: UserPreferencesInput, $organizationId: Int, $vpcSchool: String) {
      updateUserProfile(email: $email, username: $username, guardianEmail: $guardianEmail, password: $password,
        firstName: $firstName, lastName: $lastName, persona: $persona, profile: $profile, ageOfConsent: $ageOfConsent,
        preferences: $preferences, organizationId: $organizationId, vpcSchool: $vpcSchool) {
          status
          errors
          user {
            ...CoreUserFields
          }
        }
    }`

export const ACTIVATE_USER = gql`
  ${USER_FRAGMENT}
  mutation activateUser($confirmationToken: String) {
    activateUser(confirmationToken: $confirmationToken) {
      errors
      status
      user {
        ...CoreUserFields
      }
      token
    }
  }
  `

export const CLEVER_SIGNIN = gql`
${USER_FRAGMENT}
  mutation SignInByCode($code: String!) {
    signInByCode(code: $code) {
      errors
      status
      token
      user {
        ...CoreUserFields
      }
    }
  }
`

export const MASQUERADE_SIGNIN = gql`
${USER_FRAGMENT}
  mutation SignInByMasquerade($masquerade: String!) {
    signInByMasquerade(masquerade: $masquerade) {
      errors
      status
      token
      user {
        ...CoreUserFields
      }
    }
  }
`
